import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Typography,
  Chip,
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  tacticalAccordionSx,
  tacticalSummarySx,
  tacticalChipSx,
  tacticalActionSx,
  tableHeadCellSx as headCellSx,
  tableBodyCellSx as bodyCellSx,
  microLabelSx,
} from "../../utils/fdcTheme";
import { formatTimeOnly, SHOT_TIMELINE_STAGES, latestStageOf } from "../../utils/fdcHelpers";

/**
 * Compare a shot to the safety clearance defined for its (projectile, fuze_type).
 * Returns {} if the shot is safe (or no clearance applies); otherwise returns an
 * object whose keys are the violated dimensions, each mapped to a short message.
 *
 * Direction is treated as a closed arc on the [0, 6400) mil compass:
 *   - if direction_left <= direction_right, "safe" means direction_left <= dir <= direction_right
 *   - if direction_left > direction_right, the safe arc wraps through 0 (e.g. left=6000, right=400
 *     means 6000..6400..0..400 is safe). We honor that automatically.
 */
function evaluateSafety(shot, clearances) {
  if (!shot || !Array.isArray(clearances) || clearances.length === 0) return {};
  const proj = String(shot.projectile || "").toUpperCase();
  const fuze = String(shot.fuze_type || "").toUpperCase();
  const c = clearances.find(
    (x) => String(x.projectile || "").toUpperCase() === proj && String(x.fuze_type || "").toUpperCase() === fuze,
  );
  if (!c) return {};

  const out = {};
  const qe = Number(shot.qe);
  if (Number.isFinite(qe)) {
    if (c.qe_min != null && qe < Number(c.qe_min)) out.qe = `QE ${qe} below min ${c.qe_min}`;
    if (c.qe_max != null && qe > Number(c.qe_max)) out.qe = `QE ${qe} above max ${c.qe_max}`;
  }

  const dir = Number(shot.fire_direction_deflection);
  if (Number.isFinite(dir) && c.direction_left != null && c.direction_right != null) {
    const L = Number(c.direction_left);
    const R = Number(c.direction_right);
    let inside;
    if (L <= R) inside = dir >= L && dir <= R;
    else inside = dir >= L || dir <= R; // wraps through 0
    if (!inside) out.direction = `Dir ${dir} outside arc ${L}–${R}`;
  }

  if (fuze === "TIME") {
    const fs = Number(shot.fuze_setting);
    if (Number.isFinite(fs) && c.fuze_setting_min != null && fs < Number(c.fuze_setting_min)) {
      out.fuze_setting = `FS ${fs} below min ${c.fuze_setting_min}`;
    }
  }

  return out;
}

const numCellSx = {
  fontVariantNumeric: "tabular-nums",
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: 0.2,
};

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function StatusBadge({ shot }) {
  if (!shot.confirmed) {
    return (
      <Box
        component="span"
        sx={(t) => ({
          display: "inline-flex",
          alignItems: "center",
          px: 0.9,
          py: 0.2,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.18 : 0.12),
          color: t.palette.warning.main,
          border: "1px solid",
          borderColor: alpha(t.palette.warning.main, 0.4),
        })}
      >
        Pending
      </Box>
    );
  }
  if (shot.acknowledged) {
    return (
      <Box
        component="span"
        sx={(t) => ({
          display: "inline-flex",
          alignItems: "center",
          px: 0.9,
          py: 0.2,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          bgcolor: alpha(t.palette.success.main, t.palette.mode === "dark" ? 0.18 : 0.12),
          color: t.palette.success.main,
          border: "1px solid",
          borderColor: alpha(t.palette.success.main, 0.4),
        })}
      >
        Roger
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={(t) => ({
        display: "inline-flex",
        alignItems: "center",
        px: 0.9,
        py: 0.2,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        bgcolor: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.08 : 0.05),
        color: t.palette.text.secondary,
        border: "1px solid",
        borderColor: alpha(t.palette.divider, 0.95),
      })}
    >
      Sent
    </Box>
  );
}

export default function FiringDataPanel({
  expanded,
  onChange,
  hasMission,
  shots,
  selectedShotId,
  setSelectedShotId,
  onConfirmShots,
  onDeleteShots,
  safetyClearances = [],
}) {
  const [checkedShotIds, setCheckedShotIds] = useState(new Set());
  // Per-shot safety overrides. A shot violates the clearance until the operator
  // explicitly clicks "Clear warning", at which point its id lands in this Set
  // and the Send-to-Guns gate is opened for it. Cleared on unmount / mission change.
  const [overriddenShotIds, setOverriddenShotIds] = useState(new Set());

  // Pre-compute the safety verdict for every shot once per render. Empty object = safe.
  const safetyByShot = useMemo(() => {
    const map = new Map();
    for (const s of shots) map.set(s.shot_id, evaluateSafety(s, safetyClearances));
    return map;
  }, [shots, safetyClearances]);

  // If the clearance changes such that an already-overridden shot becomes safe,
  // drop the now-stale override (it'll just confuse the UI otherwise).
  useEffect(() => {
    setOverriddenShotIds((prev) => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        const verdict = safetyByShot.get(id);
        if (verdict && Object.keys(verdict).length > 0) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [safetyByShot]);

  const overrideShot = (shotId) => {
    setOverriddenShotIds((prev) => {
      const next = new Set(prev);
      next.add(shotId);
      return next;
    });
  };

  const isShotSendable = (shot) => {
    const verdict = safetyByShot.get(shot.shot_id);
    if (!verdict || Object.keys(verdict).length === 0) return true;
    return overriddenShotIds.has(shot.shot_id);
  };

  const sortedShots = useMemo(
    () =>
      shots.slice().sort((a, b) => {
        const gunA = a.gun_id ?? "zzz";
        const gunB = b.gun_id ?? "zzz";
        if (gunA !== gunB) return String(gunA).localeCompare(String(gunB), undefined, { numeric: true });
        // Latest shot first within each gun's group.
        return (b.shot_id ?? 0) - (a.shot_id ?? 0);
      }),
    [shots],
  );

  // Per-gun ordinal: gun 1's first/second/third shots = 1/2/3, regardless of mission-wide shot_id.
  const perGunIndex = useMemo(() => {
    const map = new Map();
    const counters = new Map();
    const byGun = [...shots].sort((a, b) => (a.shot_id ?? 0) - (b.shot_id ?? 0));
    for (const s of byGun) {
      const gun = String(s.gun_id ?? "N/A");
      const next = (counters.get(gun) ?? 0) + 1;
      counters.set(gun, next);
      map.set(`${gun}-${s.shot_id}`, next);
    }
    return map;
  }, [shots]);

  const pendingShots = useMemo(() => sortedShots.filter((s) => !s.confirmed), [sortedShots]);

  // Total rounds expended on this mission so far (only count CONFIRMED shots — pending
  // shots haven't been sent to guns yet so they don't represent consumed ammo).
  const totalRoundsConsumed = useMemo(
    () => shots.filter((s) => s.confirmed).reduce((sum, s) => sum + (Number(s.rounds) || 1), 0),
    [shots],
  );
  // Per-gun consumption totals (confirmed only), keyed by gun_id string.
  const roundsPerGun = useMemo(() => {
    const m = new Map();
    for (const s of shots) {
      if (!s.confirmed) continue;
      const key = String(s.gun_id ?? "N/A");
      m.set(key, (m.get(key) || 0) + (Number(s.rounds) || 1));
    }
    return m;
  }, [shots]);

  useEffect(() => {
    setCheckedShotIds((prev) => {
      const validIds = new Set(pendingShots.map((s) => s.shot_id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size !== prev.size ? next : prev;
    });
  }, [pendingShots]);

  const toggleChecked = (shotId) => {
    setCheckedShotIds((prev) => {
      const next = new Set(prev);
      if (next.has(shotId)) next.delete(shotId);
      else next.add(shotId);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedShotIds.size === pendingShots.length) {
      setCheckedShotIds(new Set());
    } else {
      setCheckedShotIds(new Set(pendingShots.map((s) => s.shot_id)));
    }
  };

  const checkedCount = checkedShotIds.size;
  const allPendingChecked = checkedCount > 0 && checkedCount === pendingShots.length;
  const someChecked = checkedCount > 0;

  // Which shot's timeline tooltip is currently open (mobile/touch needs click-to-open).
  const [openTimelineShotId, setOpenTimelineShotId] = useState(null);

  // Auto-close the timeline tooltip after a short pause so it never gets stuck open.
  useEffect(() => {
    if (openTimelineShotId == null) return;
    const t = setTimeout(() => setOpenTimelineShotId(null), 6000);
    return () => clearTimeout(t);
  }, [openTimelineShotId]);

  // Per-gun visibility: show only the 3 latest shots by default; the rest are hidden behind
  // a "Show N older" expander row per gun.
  const SHOWN_PER_GUN = 3;
  const [expandedGuns, setExpandedGuns] = useState(() => new Set());
  const toggleGunExpand = (gun) => {
    setExpandedGuns((prev) => {
      const next = new Set(prev);
      if (next.has(gun)) next.delete(gun);
      else next.add(gun);
      return next;
    });
  };

  // Group sortedShots by gun (preserves intra-gun order: latest-first)
  const groupedByGun = useMemo(() => {
    const map = new Map();
    for (const shot of sortedShots) {
      const gun = String(shot.gun_id ?? "N/A");
      if (!map.has(gun)) map.set(gun, []);
      map.get(gun).push(shot);
    }
    return map;
  }, [sortedShots]);

  // Build flat render list (shots + expanders) honoring the per-gun limit.
  const renderRows = useMemo(() => {
    const out = [];
    let firstGun = true;
    for (const [gun, gunShots] of groupedByGun.entries()) {
      const isExpanded = expandedGuns.has(gun);
      const visible = isExpanded ? gunShots : gunShots.slice(0, SHOWN_PER_GUN);
      const hidden = gunShots.length - visible.length;

      visible.forEach((shot, i) => {
        out.push({ type: "shot", shot, isGunChange: !firstGun && i === 0 });
      });

      if (hidden > 0 || (isExpanded && gunShots.length > SHOWN_PER_GUN)) {
        out.push({ type: "expander", gun, hidden, isExpanded });
      }

      firstGun = false;
    }
    return out;
  }, [groupedByGun, expandedGuns]);

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disabled={!hasMission}
      disableGutters
      sx={(theme) => tacticalAccordionSx(theme)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={(theme) => tacticalSummarySx(theme)}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Firing Data
          </Typography>
          <Chip
            size="small"
            label={`${shots.length} shot${shots.length === 1 ? "" : "s"}`}
            sx={(theme) => tacticalChipSx(theme, shots.length ? "accent" : "neutral")}
          />
          {totalRoundsConsumed > 0 && (
            <Chip
              size="small"
              label={`${totalRoundsConsumed} rounds fired`}
              sx={(theme) => tacticalChipSx(theme, "success")}
            />
          )}
          {pendingShots.length > 0 && (
            <Chip
              size="small"
              label={`${pendingShots.length} pending`}
              sx={(theme) => tacticalChipSx(theme, "warning")}
            />
          )}
          {(() => {
            const unsafeCount = shots.filter((s) => {
              const v = safetyByShot.get(s.shot_id);
              return v && Object.keys(v).length > 0 && !overriddenShotIds.has(s.shot_id);
            }).length;
            return unsafeCount > 0 ? (
              <Chip
                size="small"
                label={`${unsafeCount} unsafe`}
                sx={(theme) => tacticalChipSx(theme, "danger")}
              />
            ) : null;
          })()}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        {sortedShots.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No shots calculated yet.
            </Typography>
          </Box>
        ) : (
          <Box>
            {pendingShots.length > 0 && (onConfirmShots || onDeleteShots) && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                sx={(theme) => ({
                  px: 1.25,
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.9),
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.06 : 0.04),
                })}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase" }}>
                  {checkedCount} of {pendingShots.length} pending selected
                </Typography>
                <Stack direction="row" spacing={0.75}>
                  {onConfirmShots && (() => {
                    const checkedShots = pendingShots.filter((s) => checkedShotIds.has(s.shot_id));
                    const blocked = checkedShots.filter((s) => !isShotSendable(s));
                    const hasBlocked = blocked.length > 0;
                    return (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!someChecked || hasBlocked}
                        onClick={() => {
                          // Only forward shots that are safe or overridden.
                          const sendable = checkedShots.filter(isShotSendable).map((s) => s.shot_id);
                          if (sendable.length === 0) return;
                          onConfirmShots(sendable);
                          setCheckedShotIds(new Set());
                        }}
                        title={hasBlocked
                          ? `Clear safety warning on ${blocked.length} shot${blocked.length === 1 ? "" : "s"} first`
                          : ""}
                        sx={(theme) => ({
                          ...tacticalActionSx(theme, "accent", "contained"),
                          minHeight: 40,
                          fontWeight: 700,
                        })}
                      >
                        Send to Guns
                      </Button>
                    );
                  })()}
                  {onDeleteShots && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!someChecked}
                      onClick={() => {
                        onDeleteShots([...checkedShotIds]);
                        setCheckedShotIds(new Set());
                      }}
                      sx={(theme) => ({
                        ...tacticalActionSx(theme, "danger"),
                        minHeight: 40,
                        fontWeight: 700,
                      })}
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}

            <TableContainer>
              <Table size="small" sx={{ "& td, & th": { borderBottom: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={(t) => ({ ...headCellSx(t), width: 44, p: 0.5 })}>
                      {pendingShots.length > 0 && (
                        <Checkbox
                          size="medium"
                          checked={allPendingChecked}
                          indeterminate={someChecked && !allPendingChecked}
                          onChange={toggleAll}
                          icon={<RadioButtonUncheckedRoundedIcon fontSize="small" />}
                          checkedIcon={<CheckCircleRoundedIcon fontSize="small" />}
                          sx={{ p: 0.5, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "warning.main" } }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={headCellSx}>Gun</TableCell>
                    <TableCell sx={headCellSx}>Shot</TableCell>
                    <TableCell sx={headCellSx} align="right">Dir</TableCell>
                    <TableCell sx={headCellSx} align="right">Az</TableCell>
                    <TableCell sx={headCellSx} align="right">QE</TableCell>
                    <TableCell sx={headCellSx} align="right">ΔZ</TableCell>
                    <TableCell sx={headCellSx}>Proj</TableCell>
                    <TableCell sx={headCellSx}>Fuze</TableCell>
                    <TableCell sx={headCellSx} align="right">Setting</TableCell>
                    <TableCell sx={headCellSx} align="right">Rds</TableCell>
                    <TableCell sx={headCellSx}>Last Update</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderRows.map((row) => {
                    if (row.type === "expander") {
                      return (
                        <TableRow
                          key={`exp-${row.gun}`}
                          hover
                          onClick={() => toggleGunExpand(row.gun)}
                          sx={(t) => ({
                            cursor: "pointer",
                            bgcolor: alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.025 : 0.015),
                          })}
                        >
                          <TableCell
                            colSpan={10}
                            sx={(t) => ({
                              py: 0.6, px: 1,
                              fontSize: 12,
                              fontWeight: 600,
                              letterSpacing: 0.4,
                              textTransform: "uppercase",
                              color: t.palette.text.secondary,
                              borderBottom: "1px solid",
                              borderBottomColor: alpha(t.palette.divider, 0.7),
                              textAlign: "center",
                            })}
                          >
                            {row.isExpanded
                              ? `▴ Hide older shots for gun ${row.gun} · ${roundsPerGun.get(row.gun) || 0} rds fired`
                              : `▾ Show ${row.hidden} older shot${row.hidden === 1 ? "" : "s"} for gun ${row.gun} · ${roundsPerGun.get(row.gun) || 0} rds fired`}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const { shot, isGunChange } = row;
                    const isPending = !shot.confirmed;
                    const isChecked = checkedShotIds.has(shot.shot_id);
                    const isSelected = selectedShotId === shot.shot_id;
                    const safetyVerdict = safetyByShot.get(shot.shot_id) || {};
                    const isUnsafe = Object.keys(safetyVerdict).length > 0;
                    const isOverridden = overriddenShotIds.has(shot.shot_id);
                    return (
                      <React.Fragment key={`${shot.gun_id}-${shot.shot_id}`}>
                      <TableRow
                        hover
                        onClick={() => {
                          setSelectedShotId(shot.shot_id);
                          if (isPending) toggleChecked(shot.shot_id);
                        }}
                        selected={isSelected}
                        sx={(t) => ({
                          cursor: "pointer",
                          minHeight: 48,
                          // Unsafe-and-not-overridden shots get a red wash so the
                          // operator can't miss them.
                          ...(isUnsafe && !isOverridden && {
                            bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.18 : 0.1),
                            "&:hover": {
                              bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.24 : 0.14),
                            },
                          }),
                          // Overridden shots: lighter red tint with a dashed marker.
                          ...(isUnsafe && isOverridden && {
                            bgcolor: alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.08 : 0.05),
                          }),
                          ...(isGunChange && {
                            "& td": {
                              borderTop: "2px solid",
                              borderTopColor: alpha(t.palette.divider, 0.9),
                            },
                          }),
                          // Left edge marker: red for unsafe, yellow for pending, none otherwise.
                          ...((isUnsafe || isPending) && {
                            "& td:first-of-type": {
                              borderLeft: "3px solid",
                              borderLeftColor: isUnsafe
                                ? t.palette.error.main
                                : t.palette.warning.main,
                              borderLeftStyle: isUnsafe && isOverridden ? "dashed" : "solid",
                            },
                          }),
                          "&.Mui-selected": {
                            bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.1 : 0.06),
                          },
                          "&.Mui-selected:hover": {
                            bgcolor: alpha(t.palette.warning.main, t.palette.mode === "dark" ? 0.14 : 0.09),
                          },
                        })}
                      >
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), p: 0.5 })} onClick={(e) => e.stopPropagation()}>
                          {isPending ? (
                            <Checkbox
                              size="medium"
                              checked={isChecked}
                              onChange={() => toggleChecked(shot.shot_id)}
                              icon={<RadioButtonUncheckedRoundedIcon fontSize="small" />}
                              checkedIcon={<CheckCircleRoundedIcon fontSize="small" />}
                              sx={{ p: 0.5, "&.Mui-checked": { color: "warning.main" } }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), fontWeight: 700 })}>
                          {shot.gun_id ?? "N/A"}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx })}>
                          #{perGunIndex.get(`${String(shot.gun_id ?? "N/A")}-${shot.shot_id}`) ?? shot.shot_id}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx })} align="right">
                          {formatValue(shot.fire_direction_deflection)}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx })} align="right">
                          {formatValue(shot.fire_azimuth)}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx })} align="right">
                          {formatValue(shot.qe)}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx, color: t.palette.text.secondary })} align="right">
                          {formatValue(shot.dz)}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), fontWeight: 700 })}>
                          {shot.projectile || "HE"}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), fontWeight: 700 })}>
                          {shot.fuze_type || "PD"}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx })} align="right">
                          {formatValue(shot.fuze_setting)}
                        </TableCell>
                        <TableCell sx={(t) => ({ ...bodyCellSx(t), ...numCellSx, fontWeight: 700 })} align="right">
                          {shot.rounds ?? 1}
                        </TableCell>
                        <TableCell
                          sx={(t) => ({ ...bodyCellSx(t), color: t.palette.text.secondary, cursor: "pointer" })}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenTimelineShotId((prev) => (prev === shot.shot_id ? null : shot.shot_id));
                          }}
                        >
                          {(() => {
                            const latest = latestStageOf(shot);
                            if (!latest) return "—";
                            const totalRounds = Number(shot.rounds) || 1;
                            const lines = SHOT_TIMELINE_STAGES
                              .filter((s) => !(s.key === "rounds_complete_at" && totalRounds <= 1))
                              .map((s) => {
                                const ts = shot[s.key];
                                return {
                                  label: s.label,
                                  time: ts ? formatTimeOnly(ts) : "—",
                                  reached: !!ts,
                                };
                              });
                            const isOpen = openTimelineShotId === shot.shot_id;
                            return (
                              <Tooltip
                                arrow
                                placement="bottom-end"
                                open={isOpen}
                                onClose={() => setOpenTimelineShotId((prev) => (prev === shot.shot_id ? null : prev))}
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                                slotProps={{
                                  popper: {
                                    modifiers: [
                                      { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
                                      { name: "flip", options: { fallbackPlacements: ["top-end", "bottom-start", "top-start"] } },
                                    ],
                                  },
                                }}
                                title={
                                  <Box sx={{ minWidth: 180, py: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.5, color: "rgba(255,255,255,0.85)" }}>
                                      Shot Timeline · Gun {shot.gun_id ?? "?"}
                                    </Typography>
                                    {lines.map((l) => (
                                      <Stack
                                        key={l.label}
                                        direction="row"
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: 700,
                                          fontVariantNumeric: "tabular-nums",
                                          opacity: l.reached ? 1 : 0.45,
                                          py: 0.15,
                                        }}
                                      >
                                        <span>{l.reached ? "✓" : "·"} {l.label}</span>
                                        <span>{l.time}</span>
                                      </Stack>
                                    ))}
                                    <Typography
                                      sx={{
                                        mt: 0.5, pt: 0.5,
                                        fontSize: 10,
                                        fontStyle: "italic",
                                        opacity: 0.7,
                                        borderTop: "1px solid rgba(255,255,255,0.15)",
                                      }}
                                    >
                                      Tap again to close
                                    </Typography>
                                  </Box>
                                }
                              >
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                  <Box
                                    component="span"
                                    sx={(t) => ({
                                      ...microLabelSx,
                                      fontSize: 9,
                                      color: latest.key === "rounds_complete_at" || latest.key === "shot_complete_at"
                                        ? t.palette.success.main
                                        : t.palette.text.secondary,
                                    })}
                                  >
                                    {latest.label}
                                  </Box>
                                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
                                    {formatTimeOnly(latest.iso)}
                                  </span>
                                </Box>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                      {isUnsafe && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            sx={(t) => ({
                              p: 0.75,
                              borderBottom: "1px solid",
                              borderBottomColor: alpha(t.palette.divider, 0.7),
                              bgcolor: isOverridden
                                ? alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.06 : 0.04)
                                : alpha(t.palette.error.main, t.palette.mode === "dark" ? 0.14 : 0.08),
                            })}
                          >
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "stretch", sm: "center" }}
                              justifyContent="space-between"
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                <WarningAmberRoundedIcon sx={{ fontSize: 18, color: "error.main", flexShrink: 0 }} />
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    color: "error.main",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {isOverridden ? "Override active" : "Outside safety clearance"}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>
                                  {Object.values(safetyVerdict).join(" · ")}
                                </Typography>
                              </Stack>
                              {!isOverridden && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="error"
                                  onClick={(e) => { e.stopPropagation(); overrideShot(shot.shot_id); }}
                                  sx={{
                                    minHeight: 32,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    textTransform: "none",
                                    borderRadius: 1,
                                    flexShrink: 0,
                                  }}
                                >
                                  Clear warning &amp; allow
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
